/**
 * Encryption at rest for settings marked `encrypted` (currently the AI
 * provider keys — see settings/pages/ai.ts).
 *
 * They are stored as one AES-GCM blob under a separate localStorage key,
 * encrypted with the *project* key — the random per-project key that
 * projects.ts unwraps with the project password at unlock time and holds in
 * memory only. So the secrets are readable exactly when a project is open on
 * this device, and a copy of localStorage on its own yields nothing.
 *
 * This is the same key the QR transfer code is encrypted with
 * (transferCode.ts), which is what makes a scan work between two devices
 * holding the same project and nowhere else.
 *
 * Lifecycle is driven by watching projectStore's `projectKey` rather than by
 * calling into the unlock paths: ProjectSetup.tsx enters a project from four
 * different places (local unlock, cloud recovery, import, create) and every
 * one of them ends in the same store write.
 */

import Cipher from "../data/crypto/cipher";
import { useProjectStore } from "../store/projectStore";
import {
  encryptedSettingKeys,
  setSecretsPersister,
  useSettingsStore,
} from "./settingsStore";
import type { SettingValue } from "./types";

const SECRETS_KEY = "yaqadocs.settings.secrets.v1";

type SettingsValues = Record<string, Record<string, SettingValue>>;

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return window.btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = window.atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function readBlob(): string | null {
  try {
    return window.localStorage.getItem(SECRETS_KEY);
  } catch {
    return null;
  }
}

function writeBlob(value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(SECRETS_KEY);
    else window.localStorage.setItem(SECRETS_KEY, value);
  } catch {
    // localStorage unavailable — the secrets stay in memory for this session.
  }
}

/** Encrypts the in-memory secrets back to localStorage. */
async function persistSecrets(): Promise<void> {
  const { projectKey } = useProjectStore.getState();
  const { secrets, secretsUnlocked } = useSettingsStore.getState();
  if (!projectKey || !secretsUnlocked) return;
  const encrypted = await Cipher.encryptWithKey(JSON.stringify(secrets), projectKey);
  writeBlob(toBase64(encrypted));
}

/**
 * Moves any values already sitting in plaintext under an `encrypted` key into
 * the secrets map and rewrites the plaintext store without them.
 *
 * This runs on every unlock, not just once: it covers the upgrade from before
 * these settings were encrypted, and it also cleans up after a downgrade or a
 * synced settings file that reintroduced a plaintext copy. Returns true when
 * something actually moved.
 */
function migratePlaintextSecrets(secrets: SettingsValues): boolean {
  const { values, replaceValues } = useSettingsStore.getState();
  let moved = false;
  const nextValues: SettingsValues = { ...values };

  for (const [pageId, key] of encryptedSettingKeys()) {
    const plain = nextValues[pageId]?.[key];
    if (plain === undefined) continue;
    // The encrypted copy is authoritative — a plaintext leftover is stale.
    if (secrets[pageId]?.[key] === undefined) {
      secrets[pageId] = { ...secrets[pageId], [key]: plain };
    }
    const page = { ...nextValues[pageId] };
    delete page[key];
    nextValues[pageId] = page;
    moved = true;
  }

  if (moved) replaceValues(nextValues);
  return moved;
}

async function unlock(projectKey: CryptoKey): Promise<void> {
  let secrets: SettingsValues = {};

  const blob = readBlob();
  if (blob) {
    try {
      secrets = JSON.parse(await Cipher.decryptWithKey(fromBase64(blob), projectKey));
    } catch {
      // Wrong project, or a corrupted blob. Starting empty is the only safe
      // move — overwriting would destroy the other project's secrets, so the
      // blob is left alone until something is actually saved.
      console.warn("[settings] Could not decrypt stored secrets for this project.");
      secrets = {};
    }
  }

  const migrated = migratePlaintextSecrets(secrets);
  useSettingsStore.getState().setSecrets(secrets, true);
  if (migrated) await persistSecrets();
}

function lock(): void {
  useSettingsStore.getState().setSecrets({}, false);
}

/**
 * Starts watching the active project. Called once at boot (src/index.tsx).
 * Safe to call more than once — later calls replace the subscription.
 */
let unsubscribe: (() => void) | null = null;

export function initSettingsSecrets(): void {
  setSecretsPersister(persistSecrets);
  unsubscribe?.();

  let lastKey: CryptoKey | null = null;
  const apply = (projectKey: CryptoKey | null) => {
    if (projectKey === lastKey) return;
    lastKey = projectKey;
    if (projectKey) void unlock(projectKey);
    else lock();
  };

  apply(useProjectStore.getState().projectKey);
  unsubscribe = useProjectStore.subscribe((state) => apply(state.projectKey));
}
